import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { AdminTripsService } from './adminTripsApi';
import { ITripsListState } from '../../../interfaces/trips/ITripsListState';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';

const initialState: ITripsListState = {
    tripsActiveAreLoading: false,
    tripsAreLoading: false,
    tripIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchAllTrips = createAsyncThunk<any, any>(
    'trips/tryToFetchAllTrips',
    async (data: any) => {
        const result = await AdminTripsService.tryToFetchAllTrips(data);
        return result?.data;
    }
);

export const tryToFetchAllJourneys = createAsyncThunk<any, void>(
    'trips/tryToFetchAllJourneys',
    async () => {
        const result = await AdminTripsService.tryToFetchAllJourneys();
        return result?.data;
    }
);

export const tryToFetchTrips = createAsyncThunk<any, IPaginationPayload>(
    'trips/tryToFetchTrips',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await AdminTripsService.tryToFetchTrips(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToFetchActiveTrips = createAsyncThunk<any, IPaginationPayload>(
    'trips/tryToFetchActiveTrips',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await AdminTripsService.tryToFetchTrips(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result;
    },
);

export const tryToCreateTrip = createAsyncThunk<any, any>(
    'trips/tryToCreateTrip',
    async (data, { rejectWithValue }) => {
        try {
            const result = await AdminTripsService.tryToCreateTrip(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditTrip = createAsyncThunk<any, any>(
    'trips/tryToEditTrip',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await AdminTripsService.tryToEditTrip(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchSingleTrip = createAsyncThunk<any, string>(
    'trips/tryToFetchSingleTrip',
    async (id: string) => {
        const result = await AdminTripsService.tryToFetchSingleTrip(id);
        return result?.data;
    }
);

export const tryToFetchCabins = createAsyncThunk<any, void>(
    'trips/tryToFetchCabins',
    async () => {
        const result = await AdminTripsService.tryToFetchCabins();
        return result?.data;
    }
);

export const tryToFetchTrains = createAsyncThunk<any, void>(
    'trips/tryToFetchTrains',
    async () => {
        const result = await AdminTripsService.tryToFetchTrains();
        return result?.data?.data;
    }
);

export const tryToFetchCarriageTemplates = createAsyncThunk<any, void>(
    'trips/tryToFetchCarriageTemplates',
    async () => {
        const result = await AdminTripsService.tryToFetchCarriageTemplates();
        return result?.data?.data || result?.data;
    }
);

export const tryToFetchCarriageTemplatesWithCabinOptions = createAsyncThunk<any, string | undefined>(
    'trips/tryToFetchCarriageTemplatesWithCabinOptions',
    async (tripId) => {
        const result = await AdminTripsService.tryToFetchCarriageTemplatesWithCabinOptions(tripId);
        return result?.data?.data || result?.data;
    }
);

export const tryToGetFeedbackStats = createAsyncThunk<any, any>(
    'trips/tryToGetFeedbackStats',
    async (data: any) => {
        const result = await AdminTripsService.tryToGetFeedbackStats(data);
        return result?.data?.data;
    }
);

export const tryToUpdateTripStatus = createAsyncThunk<any, { id: string; status: string }, { rejectValue: string }>(
    'trips/tryToUpdateTripStatus',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const result = await AdminTripsService.tryToUpdateTripStatus(id, status);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message || 'Failed to update trip status');
        }
    }
);

export const tryToFetchRolesForTrip = createAsyncThunk<any, void>(
    'trips/tryToFetchRolesForTrip',
    async () => {
        const result = await AdminTripsService.tryToFetchRolesForTrip();
        return result?.data?.data || result?.data;
    }
);

export const tryToDeleteTrip = createAsyncThunk<any, string, { rejectValue: string }>(
    'trips/tryToDeleteTrip',
    async (id, { rejectWithValue }) => {
        try {
            const result = await AdminTripsService.tryToDeleteTrip(id);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message || 'Failed to delete trip');
        }
    }
);

export const adminTripsSlice = createSlice({
    name: 'adminTrips',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchTrips
            .addCase(tryToFetchTrips.pending, (state) => {
                state.tripsAreLoading = true;
            })
            .addCase(tryToFetchTrips.rejected, (state) => {
                state.tripsAreLoading = false;
            })
            .addCase(tryToFetchTrips.fulfilled, (state, action) => {
                state.tripsAreLoading = false;
                state.trips = action.payload;
            })


            // tryToFetchActiveTrips
            .addCase(tryToFetchActiveTrips.pending, (state) => {
                state.tripsActiveAreLoading = true;
            })
            .addCase(tryToFetchActiveTrips.rejected, (state) => {
                state.tripsActiveAreLoading = false;
            })
            .addCase(tryToFetchActiveTrips.fulfilled, (state, action) => {
                state.tripsActiveAreLoading = false;
                state.execuTrips = action.payload.data;
            })

            // tryToFetchSingleTrip
            .addCase(tryToFetchSingleTrip.pending, (state) => {
                state.tripIsLoading = true;
            })
            .addCase(tryToFetchSingleTrip.rejected, (state) => {
                state.tripIsLoading = false;
            })
            .addCase(tryToFetchSingleTrip.fulfilled, (state, action) => {
                state.tripIsLoading = false;
                state.trip = action.payload;
            })

    },
});

export default adminTripsSlice.reducer;